import { useState } from "react";
import { InfoNote } from "@/components/InfoNote";
import { useAchievements } from "@/os/state/achievements";
import { play } from "@/lib/sound";

const CASE_INVESTIGATION =
    "i treated the 2–3 minutes as a symptom, not a personality trait. measured where a run actually spent its time, interrogated every step with 'what does this do, and does anyone remember why?', and kept a suspects list. no single dramatic villain — just a pile of small waits that had quietly agreed to add up.";

const CASE_SOLUTION =
    "no single dramatic fix — a series of small ones, each verified before moving to the next. the fact on record: same legacy app, same job, execution down from 2–3 minutes to under 10 seconds.";

const CASE_LESSON =
    "slow isn't a property of old software — it's an accumulation of decisions nobody re-read. measure before you judge, kill the boring waits first, and never assume the original author was wrong; they were just busy. also: users don't experience your architecture. they experience the wait.";

const STAGE_NAMES = ["the problem", "the investigation", "the solution", "the lesson"];

export function BugsIFixed() {
    const [openCase, setOpenCase] = useState(false);
    const [stage, setStage] = useState<number | null>(null);
    const { trackStage } = useAchievements();

    return (
        <div data-testid="bugs-i-fixed">
            <div className="flex flex-wrap items-center gap-2">
                <span className="badge border-coral text-coral">case_001 // performance</span>
                <span className="badge">status: solved</span>
            </div>
            <h2 className="mt-3 font-display text-2xl font-extrabold text-ink">The Two-Minute Problem.</h2>
            <p className="text-[13px] text-inksoft">How a slow legacy application became a much faster one.</p>

            <div className="mt-4 rounded-2xl border-2 border-ink bg-sandsoft/50 p-4" data-testid="before-after">
                <div className="flex items-center gap-3">
                    <span className="w-12 flex-none font-mono text-[10px] text-inkfaint">BEFORE</span>
                    <div className="h-5 flex-1 overflow-hidden rounded-full border-2 border-ink bg-paper">
                        <div className="h-full rounded-full bg-coral" style={{ width: "96%" }} />
                    </div>
                    <span className="w-24 flex-none text-right font-mono text-[11px]">2–3 minutes</span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                    <span className="w-12 flex-none font-mono text-[10px] text-inkfaint">AFTER</span>
                    <div className="h-5 flex-1 overflow-hidden rounded-full border-2 border-ink bg-paper">
                        <div className="h-full rounded-full bg-sage" style={{ width: "5%" }} />
                    </div>
                    <span className="w-24 flex-none text-right font-mono text-[11px] text-sage">&lt; 10 seconds</span>
                </div>
                <p className="mt-2 font-mono text-[10.5px] text-inkfaint">
                    a legacy microsoft application, optimized at EG India. same job, dramatically faster wall-clock time.
                </p>
            </div>

            {!openCase ? (
                <button
                    data-testid="investigate-case"
                    className="btn btn-blue mt-4"
                    onClick={() => {
                        setOpenCase(true);
                        setStage(0);
                        trackStage(0);
                        play("click");
                    }}
                >
                    Investigate the case.
                </button>
            ) : (
                <div className="mt-4" data-testid="case-stages">
                    <div className="flex flex-wrap gap-2">
                        {STAGE_NAMES.map((s, i) => (
                            <button
                                key={s}
                                data-testid={`case-stage-${i}`}
                                className={`chip ${stage === i ? "border-elec bg-elecsoft text-ink" : ""}`}
                                onClick={() => {
                                    setStage(i);
                                    trackStage(i);
                                    play("click");
                                }}
                            >
                                {i + 1}. {s}
                            </button>
                        ))}
                    </div>
                    <div className="mt-3 rounded-2xl border border-sand bg-paper p-4" data-testid="case-stage-content">
                        {stage === 0 && (
                            <p className="text-[13px] leading-relaxed text-inksoft">
                                a legacy microsoft application used for construction estimates took 2–3 minutes to execute. in
                                developer time, that's a coffee. in user time, that's a wall.
                            </p>
                        )}
                        {stage === 1 && (
                            <div>
                                <p className="mb-2 font-mono text-[11px] font-bold">the investigation</p>
                                <InfoNote text={CASE_INVESTIGATION} testId="case-investigation" />
                            </div>
                        )}
                        {stage === 2 && (
                            <div>
                                <p className="mb-2 font-mono text-[11px] font-bold">the solution</p>
                                <InfoNote text={CASE_SOLUTION} testId="case-solution" />
                            </div>
                        )}
                        {stage === 3 && (
                            <div>
                                <p className="mb-2 font-mono text-[11px] font-bold">the lesson</p>
                                <InfoNote text={CASE_LESSON} testId="case-lesson" />
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="mt-5 border-t border-sand pt-4" data-testid="track-record">
                <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-elec">
                    the rest of the case files
                </p>
                <p className="mt-1 text-[13px] leading-relaxed text-inksoft">
                    case_001 is the one with a stopwatch attached. the others are quieter — the kind of work that only shows
                    up as numbers at the bottom of a sprint:
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    {RECORD.map((r) => (
                        <div key={r.label} className="rounded-xl border border-sand bg-sandsoft/50 p-3">
                            <p className="font-display text-xl font-extrabold text-ink">{r.stat}</p>
                            <p className="font-mono text-[10.5px] leading-snug text-inkfaint">{r.label}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-3 font-mono text-[10.5px] text-inkfaint">
                    ~70 of those issues were found by actually using the product, not by reading the ticket. highly
                    recommended.
                </p>
            </div>
        </div>
    );
}

const RECORD = [
    { stat: "85+", label: "pull requests authored in the extraction platform (170+ merged overall)" },
    { stat: "60+", label: "backend test modules keeping the pipeline honest" },
    { stat: "~70", label: "product issues identified and resolved in the estimation platform" },
    { stat: "200+", label: "frontend unit-test files in the angular 21 codebase" },
    { stat: "5", label: "xUnit test projects on the .net 10 side" },
    { stat: "3", label: "trades parsed — plumbing (VVS), masonry, painting" },
];
